import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSlackConnect } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";
import { LoadingState, MutationError } from "../components/ui";
import { viewAsUrl } from "../viewAs";

/** What the state says about where it came from - for finding the way back only; the API is what trusts it, by its signature. */
export function slackStateOrigin(state: string | null): { organisationId: string; environmentId: string } | undefined {
  const payload = state?.split(".")[0];
  if (!payload) return undefined;
  try {
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const parsed = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "="))) as { o?: unknown; e?: unknown };
    return typeof parsed.o === "string" && typeof parsed.e === "string" ? { organisationId: parsed.o, environmentId: parsed.e } : undefined;
  } catch {
    return undefined;
  }
}

/**
 * #568: where Slack sends the browser back after Add to Slack. The page is
 * signed in, so it finishes the connection through the API under the
 * person's own token - there is no unauthenticated callback - then returns
 * to Access › Slack notifications on the environment it started from.
 * Gairm staff who started inside a customer's portal are taken back into
 * that view first, where their editing mode is kept for the tab.
 */
export function SlackCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const connect = useSlackConnect();
  const sent = useRef(false);
  const state = params.get("state");
  const origin = slackStateOrigin(state);
  const intoView = Boolean(me && me.role === "internal-team" && !me.viewAs && origin);

  useEffect(() => {
    if (!me || sent.current) return;
    sent.current = true;
    if (intoView) {
      window.location.replace(viewAsUrl(origin!.organisationId, `/slack/callback?${params.toString()}`));
      return;
    }
    if (!state) {
      navigate("/access?slack=invalid", { replace: true });
      return;
    }
    const code = params.get("code");
    const error = params.get("error");
    connect.mutate(
      { state, ...(code ? { code } : {}), ...(error ? { error: error.slice(0, 80) } : {}) },
      {
        onSuccess: (r) => {
          const back = new URLSearchParams({ slack: r.result, ...(r.channel ? { channel: r.channel } : {}) });
          // A refused state names no environment the API will vouch for; the page it claims is still the way back.
          const environmentId = r.environmentId ?? origin?.environmentId;
          navigate(environmentId ? `/access/${encodeURIComponent(environmentId)}/slack?${back.toString()}` : `/access?${back.toString()}`, { replace: true });
        },
      },
    );
  }, [me, intoView, origin, params, state, navigate, connect]);

  return (
    <div className="mx-auto max-w-lg space-y-4 p-6">
      <LoadingState label="Connecting Slack" />
      <MutationError of={connect} />
    </div>
  );
}
